<script setup>
import {computed, onBeforeMount, watch, ref, onMounted} from 'vue'
import {useStore} from 'vuex'
import {useRouter, useRoute} from 'vue-router'
import {onAuthStateChanged} from 'firebase/auth'

import { firebase } from '@/router'
import WsHandler from '@/services/wsHandler.vue'

import Toaster from '@/components/ui/toast/Toaster.vue'
import {useToast} from '@/components/ui/toast/use-toast'
import { Header}  from '@/components/header'
import { Footer } from '@/components/footer'
import { MainMap, MapSettings, Navigator } from '@/components/map'
import { LayoutDialog, LayoutSidebar } from "@/layouts"
import { SelectedFilters, DatePicker } from '@/components/filters'
import { Breadcrumbs } from '@/components/breadcrumbs'
import {SplitPane} from '@/components/splitpane'
import {ProjectList, InviteList} from '@/components/project-list'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {Folders, Layers as LayersIcon, FileText, X} from 'lucide-vue-next'
import {OrgLayers} from '@/components/layers'
import {ReportsList} from '@/components/reports'
import { Spinner } from '@/components/ui/spinner'
import Map3DSwitch from '@/components/map/Map3DSwitch.vue'
const { toast } = useToast()

const router = useRouter()
const route = useRoute()
const store = useStore()
const currentRoute = computed(() => route.name)

const projectId = computed(() => parseInt(route.params.project_id))
const orgId = computed(() => parseInt(route.params.org_id))
const member = computed(() => store.getters['user/getMember'])

const isLoggedIn = computed(() => store.getters.getIsLoggedIn)
const isLoadingData = computed(() => store.getters.isLoadingData)
const selectedOrgId = computed(() => store.getters['org/getSelected'])
const showBackgroundUI = computed(() => route.meta.showBackgroundUI !== false)

const isDragging = ref(false)

const activeTab = computed({
  get() {
    if (route.name === 'OrgProjects') return 'projects'
    if (route.name === 'OrgLayers' || route.name === 'LayerCreate' || route.name === 'LayerDetail') return 'layers'
    if (route.name === 'OrgReports' || route.name === 'ReportDetail') return 'reports'
    return 'projects' // default
  },
  set(value) {
    navigateToTab(value)
  }
})

const navigateToTab = async (tab) => {
  const currentOrgId = orgId.value || selectedOrgId.value

  if (!currentOrgId) {
    // If no org is selected, load orgs and select the first one
    await store.dispatch('org/fetchOrgs')
    const orgs = store.getters['org/getOrgs']
    if (orgs.length > 0) {
      const firstOrgId = orgs[0].organization_id
      await store.dispatch('org/setSelectedOrg', firstOrgId.toString())

      if (tab === 'projects') {
        router.push({ name: 'OrgProjects', params: { org_id: firstOrgId } })
      } else if (tab === 'layers') {
        router.push({ name: 'OrgLayers', params: { org_id: firstOrgId } })
      } else if (tab === 'reports') {
        router.push({ name: 'OrgReports', params: { org_id: firstOrgId } })
      }
    }
    return
  }

  if (tab === 'projects') {
    router.push({ name: 'OrgProjects', params: { org_id: currentOrgId } })
  } else if (tab === 'layers') {
    router.push({ name: 'OrgLayers', params: { org_id: currentOrgId } })
  } else if (tab === 'reports') {
    router.push({ name: 'OrgReports', params: { org_id: currentOrgId } })
  }
}



onBeforeMount(async () => {
  onAuthStateChanged(firebase.auth, async (user) => {
    if (route.name === 'Invite') {
      return
    }

    if (user) {
      await store.dispatch('user/setupSettings')
      await store.dispatch('user/setUser', user)

      try {
        const firebaseToken = await firebase.auth.currentUser.getIdToken(true)
        store.commit('SET_FIREBASE_TOKEN', firebaseToken)
        const result = await store.dispatch('user/handleAuthentication', {
          firebaseToken
        })

        if (result.success) {
          await store.dispatch('org/fetchOrgs')
          const orgs = store.getters['org/getOrgs']

          if (orgs.length === 0 || orgs.length > 1) {
            if (!currentRoute.value.startsWith('Auth')) return
            router.push({name: 'AuthOverview'})
          } else if (orgs.length === 1) {
            const org = orgs[0]
            router.push({ name: 'Organization', params: { org_id: org.organization_id } })
          }
        } else {
          router.push({name: 'AuthOverview'})
        }
      } catch (e) {
        logOut()
      }
    } else {
      logOut()
    }
  })
})

const appHeight = () => document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`)

onMounted(async () => {
  await router.isReady()
  store.commit('search/SET_PARAMS', route.query)

  window.addEventListener('resize', appHeight)
  appHeight()
})

watch(
  () => member.value,
  async (value) => {
    if (value) {
      if (!zE) return
      zE('webWidget', 'identify', {
        name: value.display_name ? value.display_name : value.email,
        email: value.email
      })
    }
  }
)

const logOut = () => {
  store.dispatch('logout')
}

const handleDrop = (e) => {
  e.preventDefault()
  e.stopPropagation()

  isDragging.value = false

  const file = e.dataTransfer.files[0]

  if (!file) {
    return
  }

  const ext = file.name.split('.').pop().toLowerCase()
  if (!['kml', 'kmz', 'geojson', 'jpg', 'jpeg', 'png', 'heic', 'heif', 'avif', 'webp', 'gpx', 'fit'].includes(ext)) {
    toast({ title: "Unsupported file format", variant: 'destructive' })
    return
  }

  store.commit('geo/SET_FILES', e.dataTransfer.files)

  if (route.name !== 'FileViewer') {
    router.push({name: 'FileViewer'})
  }
}
</script>

<template>
<div v-if="showBackgroundUI">
  <div v-if="isLoggedIn" class="z-40 h-[50px] shadow-sm">
    <ws-handler></ws-handler>
    <Header />
  </div>
  <div v-else class="h-[50px] bg-gray-50"></div>
</div>

<main class="h-full z-40 text-sm main-section relative" role="main" aria-label="Main application content" id="main-content" :class="{ 'no-background-ui': !showBackgroundUI }">
  <!-- Background UI with Left Panel and Map -->
  <SplitPane v-if="showBackgroundUI">
    <template v-slot:left>
      <nav class="min-w-[330px] bg-white" role="navigation" aria-label="Projects and layers navigation">
        <Tabs v-model="activeTab" class="w-full">
          <div class="px-1">
            <TabsList
              class="grid w-full"
              :class="{'grid-cols-3': orgId === 1, 'grid-cols-2': orgId !== 1}"
              role="tablist"
              aria-label="Dashboard navigation tabs"
            >
              <TabsTrigger
                value="projects"
                role="tab"
                :aria-selected="activeTab === 'projects'"
                aria-controls="projects-panel"
                data-testid="projects-tab"
                @click="navigateToTab('projects')"
              >
                <div class="flex items-center">
                  <Folders class="w-5 h-5 mr-1.5" aria-hidden="true" />
                  <span>Projects</span>
                </div>
              </TabsTrigger>
              <TabsTrigger
                value="layers"
                role="tab"
                :aria-selected="activeTab === 'layers'"
                aria-controls="layers-panel"
                data-testid="layers-tab"
                @click="navigateToTab('layers')"
              >
                <div class="flex items-center">
                  <LayersIcon class="w-5 h-5 mr-1.5" aria-hidden="true" />
                  <span>Layers</span>
                </div>
              </TabsTrigger>

              <!-- Reports tab is only visible for orgId === 1(EarthPBC) until demo -->
              <TabsTrigger
                v-if="orgId === 1"
                value="reports"
                role="tab"
                :aria-selected="activeTab === 'reports'"
                aria-controls="reports-panel"
                data-testid="reports-tab"
                @click="navigateToTab('reports')"
              >
                <div class="flex items-center">
                  <FileText class="w-5 h-5 mr-1.5" aria-hidden="true" />
                  <span>Reports</span>
                </div>
              </TabsTrigger>
            </TabsList>
          </div>
          <TabsContent
            value="projects"
            role="tabpanel"
            id="projects-panel"
            aria-labelledby="projects-tab"
            data-testid="projects-panel"
          >
            <div class="px-3">
              <InviteList />
            </div>
            <ProjectList v-if="activeTab === 'projects'" />
          </TabsContent>
          <TabsContent
            value="layers"
            role="tabpanel"
            id="layers-panel"
            aria-labelledby="layers-tab"
            data-testid="layers-panel"
          >
            <OrgLayers v-if="activeTab === 'layers'" :key="activeTab" />
          </TabsContent>
          <!-- Reports tab is only visible for orgId === 1(EarthPBC) until demo -->
          <TabsContent
            v-if="orgId === 1"
            value="reports"
            role="tabpanel"
            id="reports-panel"
            aria-labelledby="reports-tab"
            data-testid="reports-panel"
          >
            <ReportsList v-if="activeTab === 'reports'" :key="activeTab" />
          </TabsContent>
        </Tabs>
      </nav>
    </template>

    <template v-slot:right>
      <div
        class="w-full h-full flex flex-col relative bg-gray-50"
        @dragenter="isDragging = true"
        role="region"
        aria-label="Map and content area"
      >
        <div class="w-full h-full relative">
          <MainMap :active-tab="activeTab" v-if="isLoggedIn" />
          <div
            v-if="isLoadingData"
            class="absolute z-50 top-14 bg-white rounded-full p-2 shadow-lg"
            style="left: 50%"
            role="status"
            aria-live="polite"
            aria-label="Loading data"
            data-testid="loading-indicator"
          >
            <Spinner class="w-6 h-6" aria-hidden="true" />
            <span class="sr-only">Loading data...</span>
          </div>

          <div
            v-if="isLoggedIn && isDragging"
            @dragover.prevent
            @drop.stop.prevent="handleDrop"
            @dragleave="isDragging = false"
            class="absolute inset-0 z-10 bg-gray-600 opacity-20"
            role="region"
            aria-label="File drop zone"
            aria-describedby="drop-zone-description"
          >
            <span id="drop-zone-description" class="sr-only">
              Drop supported files here (KML, KMZ, GeoJSON, images, GPX, FIT)
            </span>
          </div>
        </div>

        <div class="absolute inset-0 z-20 pointer-events-none">
          <div class="h-full w-full flex">
            <div class="w-2/3 h-full flex flex-col p-2">
              <div v-if="isLoggedIn">
                <Breadcrumbs class="mb-2 pointer-events-auto" />
              </div>

              <div
                class="flex-1 overflow-auto mb-2 rounded-md"
                role="region"
                aria-label="Content area"
              >
                <div class="h-full">
                  <LayoutDialog v-if="$route.meta.layout === 'modal'" />
                  <div v-else-if="$route.meta.layout === 'hide'"></div>
                  <LayoutSidebar v-else />
                </div>
              </div>

              <div
                v-if="isLoggedIn && activeTab === 'layers'"
                class="pointer-events-auto w-full"
                role="navigation"
                aria-label="Map navigation controls"
              >
                <Navigator />
              </div>
            </div>

            <div class="w-1/3 h-full flex-1 p-2">
              <div
                v-if="isLoggedIn"
                class="flex flex-col items-end pointer-events-auto"
                role="complementary"
                aria-label="Map controls and filters"
              >
                <div
                  v-if="projectId"
                  class="mb-1 flex items-center space-x-2"
                  role="group"
                  aria-label="Date picker"
                >
                  <DatePicker />
                </div>
                <div
                  class="mb-1"
                  v-if="projectId"
                  role="group"
                  aria-label="Active filters"
                >
                  <SelectedFilters />
                </div>
                <div role="group" aria-label="Map settings">
                  <MapSettings />
                </div>
                <div role="group" aria-label="3D map toggle">
                  <Map3DSwitch />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </SplitPane>

  <!-- Direct router view when background UI is disabled -->
  <div v-else class="h-full w-full">
    <LayoutDialog v-if="$route.meta.layout === 'modal'" />
    <div v-else-if="$route.meta.layout === 'hide'"></div>
    <LayoutSidebar v-else />
  </div>
</main>

<footer
  class="absolute bottom-0 left-0 right-0 z-40 h-[50px] shadow-lg hidden lg:block pointer-events-auto"
  role="contentinfo"
  aria-label="Application footer"
>
  <Footer />
</footer>
<Toaster v-if="isLoggedIn" />
</template>

<style>
.main-section {
  max-height: calc(var(--app-height) - 50px);
  min-height: calc(var(--app-height) - 50px);

  @media (min-width: 1024px) {
    max-height: calc(var(--app-height) - 100px);
    min-height: calc(var(--app-height) - 100px);
  }
}

.main-section.no-background-ui {
  max-height: var(--app-height);
  min-height: var(--app-height);
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
